﻿import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import * as kanbanBoardService from '@/services/kanbanBoardService';
import KanbanColumn from './KanbanColumn';
import FunctionSwimlanes from './FunctionSwimlanes';
import useSessionState from '@/hooks/useSessionState';
import { FaChevronDown, FaChevronUp, FaUsers } from 'react-icons/fa';
import * as Sentry from '@sentry/react';

/**
 * RoutineChecksSwimlanes Component
 * Dynamically renders collapsible swimlanes based on board configuration.
 * When a board has swimlanes configured, items are grouped by template/type.
 * When no board swimlanes exist, falls back to function-based swimlanes
 * (My Direct Assignments + function groups).
 */
const RoutineChecksSwimlanes = ({
    boardId,
    items,
    statuses,
    activeStatuses,
    showClosedItems,
    closedItemsLimit,
    onLoadMoreClosed,
    propertiesMDA,
    navigate,
    needsMyAttention,
    getItemIcon,
    getItemDisplayText,
    getUserDisplayName,
    formatCardDate,
    getProjectName,
    allUsers,
    isAssignedToMyDepartment,
    onDragEnd,
    userID,
    apiFetch: parentApiFetch
}) => {
    const { apiFetch: authApiFetch } = useAuth();
    const apiFetch = parentApiFetch || authApiFetch;
    const [swimlanes, setSwimlanes] = useState([]);
    const [loading, setLoading] = useState(true);
    // Persist collapsed state per board across navigation
    const [collapsedMap, setCollapsedMap] = useSessionState('ai_board_swimlane_collapsed', {});
    const [hasBoardSwimlanes, setHasBoardSwimlanes] = useState(false);

    // Load swimlanes from API whenever boardId changes
    const loadSwimlanes = useCallback(async () => {
        if (!boardId) {
            setSwimlanes([]);
            setHasBoardSwimlanes(false);
            setLoading(false);
            return;
        }
        try {
            setLoading(true);
            const data = await kanbanBoardService.fetchSwimlanes(apiFetch, boardId);
            const swimlaneData = data || [];
            setSwimlanes(swimlaneData);
            setHasBoardSwimlanes(swimlaneData.length > 0);
        } catch (error) {
            Sentry.captureException(error);
            console.error('Failed to load swimlanes:', error);
            setSwimlanes([]);
            setHasBoardSwimlanes(false);
        } finally {
            setLoading(false);
        }
    }, [apiFetch, boardId]);

    useEffect(() => {
        (async () => {
            await loadSwimlanes();
        })();
    }, [loadSwimlanes]);

    // Match items to swimlanes based on templateKey
    const getItemSwimlane = (item) => {
        const matchingSwimlane = swimlanes.find(s => {
            const matches =
                item.templateKey === s.templateKey ||
                item.actionItemsType === s.templateKey ||
                // Additional matching for common variations
                (item.templateKey && item.templateKey.toLowerCase() === s.templateKey.toLowerCase()) ||
                (item.actionItemsType && item.actionItemsType.toLowerCase() === s.templateKey.toLowerCase());

            return matches;
        });

        return matchingSwimlane?.boardSwimlaneID || null;
    };

    // Group items by swimlane
    const itemsBySwimlane = swimlanes.reduce((acc, swimlane) => {
        acc[swimlane.boardSwimlaneID] = items.filter(item =>
            getItemSwimlane(item) === swimlane.boardSwimlaneID
        );
        return acc;
    }, {});

    // Items that don't match any swimlane (orphaned items)
    const orphanedItems = items.filter(item => !getItemSwimlane(item));

    // Toggle swimlane collapse state (persisted per board)
    const toggleSwimlane = useCallback((swimlaneId) => {
        const key = `${boardId}_${swimlaneId}`;
        setCollapsedMap(prev => ({ ...prev, [key]: !prev[key] }));
    }, [boardId, setCollapsedMap]);

    /**
     * Check if a swimlane is collapsed.
     * First swimlane (index 0) defaults to expanded; all others default to collapsed.
     */
    const isSwimlaneCollapsed = useCallback((swimlaneId, isFirst) => {
        const key = `${boardId}_${swimlaneId}`;
        if (key in collapsedMap) return collapsedMap[key];
        return !isFirst;
    }, [boardId, collapsedMap]);

    if (loading) {
        return (
            <div className="swimlanes-loading-state">
                <div className="loading-spinner" />
                <p>Loading swimlanes...</p>
            </div>
        );
    }

    // If no board swimlanes configured, fall back to function-based swimlanes
    if (!hasBoardSwimlanes) {
        return (
            <FunctionSwimlanes
                userID={userID}
                apiFetch={apiFetch}
                statuses={statuses}
                activeStatuses={activeStatuses}
                showClosedItems={showClosedItems}
                closedItemsLimit={closedItemsLimit}
                onLoadMoreClosed={onLoadMoreClosed}
                propertiesMDA={propertiesMDA}
                navigate={navigate}
                needsMyAttention={needsMyAttention}
                getItemIcon={getItemIcon}
                getItemDisplayText={getItemDisplayText}
                getUserDisplayName={getUserDisplayName}
                formatCardDate={formatCardDate}
                getProjectName={getProjectName}
                allUsers={allUsers}
                isAssignedToMyDepartment={isAssignedToMyDepartment}
                onDragEnd={onDragEnd}
            />
        );
    }

    return (
        <div className="swimlanes-container">
            {/* Sticky Status Column Headers */}
            <div className="swimlanes-status-headers">
                <div className="swimlane-header-spacer" />
                {(showClosedItems ? statuses : activeStatuses).map(status => (
                    <div key={status} className={`swimlane-status-label status-${status.toLowerCase().replace('-', '')}`}>
                        {status.toUpperCase()}
                    </div>
                ))}
            </div>

            {swimlanes.map((swimlane, index) => {
                const swimlaneItems = itemsBySwimlane[swimlane.boardSwimlaneID] || [];
                const collapsed = isSwimlaneCollapsed(swimlane.boardSwimlaneID, index === 0);

                return (
                    <div key={swimlane.boardSwimlaneID} className={`swimlane ${collapsed ? 'collapsed' : ''}`}>
                        {/* Swimlane Header with collapse toggle */}
                        <div
                            className="swimlane-header clickable"
                            style={{ borderLeftColor: swimlane.color }}
                            onClick={() => toggleSwimlane(swimlane.boardSwimlaneID)}
                        >
                            <button
                                className="swimlane-toggle-btn"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    toggleSwimlane(swimlane.boardSwimlaneID);
                                }}
                                aria-label={collapsed ? 'Expand swimlane' : 'Collapse swimlane'}
                            >
                                {collapsed ? <FaChevronDown /> : <FaChevronUp />}
                            </button>
                            <div
                                className="swimlane-indicator"
                                style={{ backgroundColor: swimlane.color }}
                            />
                            <h3>{swimlane.name}</h3>
                            <span className="swimlane-count">
                                ({swimlaneItems.length})
                            </span>
                            {/* Collapsed inline status summary */}
                            {collapsed && swimlaneItems.length > 0 && (
                                <div className="swimlane-collapsed-summary">
                                    {(showClosedItems ? statuses : activeStatuses).map(status => {
                                        const count = swimlaneItems.filter(i => i.status === status).length;
                                        if (count === 0) return null;
                                        return (
                                            <span key={status} className={`summary-badge status-${status.toLowerCase().replace('-', '')}`}>
                                                {count}
                                            </span>
                                        );
                                    })}
                                </div>
                            )}
                        </div>

                        {/* Collapsible Columns Section */}
                        {!collapsed && (
                                <div className="swimlane-columns">
                                    {(showClosedItems ? statuses : activeStatuses).map(status => {
                                        const statusItems = swimlaneItems.filter(item => item.status === status);
                                        const columnItems = status === "Closed" && showClosedItems
                                            ? statusItems.slice(0, closedItemsLimit)
                                            : statusItems;

                                        const totalClosedItems = status === "Closed" ? statusItems.length : 0;
                                        const hasMoreClosedItems = status === "Closed" && totalClosedItems > closedItemsLimit;

                                        return (
                                            <KanbanColumn
                                                key={`${swimlane.boardSwimlaneID}-${status}`}
                                                status={status}
                                                droppableId={`swimlane-${swimlane.boardSwimlaneID}-${status}`}
                                                items={columnItems}
                                                propertiesMDA={propertiesMDA}
                                                navigate={navigate}
                                                hasMore={hasMoreClosedItems}
                                                totalItems={totalClosedItems}
                                                onLoadMore={onLoadMoreClosed}
                                                needsMyAttention={needsMyAttention}
                                                getItemIcon={getItemIcon}
                                                getItemDisplayText={getItemDisplayText}
                                                getUserDisplayName={getUserDisplayName}
                                                formatCardDate={formatCardDate}
                                                getProjectName={getProjectName}
                                                allUsers={allUsers}
                                                isAssignedToMyDepartment={isAssignedToMyDepartment}
                                                hideHeader={true}
                                            />
                                        );
                                    })}
                                </div>
                        )}
                    </div>
                );
            })}

            {/* Show orphaned items if any */}
            {orphanedItems.length > 0 && (() => {
                const orphanedCollapsed = isSwimlaneCollapsed('orphaned', false);
                return (
                    <div className={`swimlane orphaned-swimlane ${orphanedCollapsed ? 'collapsed' : ''}`}>
                        <div
                            className="swimlane-header clickable"
                            onClick={() => toggleSwimlane('orphaned')}
                        >
                            <button
                                className="swimlane-toggle-btn"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    toggleSwimlane('orphaned');
                                }}
                            >
                                {orphanedCollapsed ? <FaChevronDown /> : <FaChevronUp />}
                            </button>
                            <div className="swimlane-indicator" style={{ backgroundColor: '#95a5a6' }} />
                            <FaUsers style={{ color: '#95a5a6' }} />
                            <h3>Other Items</h3>
                            <span className="swimlane-count">({orphanedItems.length})</span>
                            {orphanedCollapsed && orphanedItems.length > 0 && (
                                <div className="swimlane-collapsed-summary">
                                    {(showClosedItems ? statuses : activeStatuses).map(status => {
                                        const count = orphanedItems.filter(i => i.status === status).length;
                                        if (count === 0) return null;
                                        return (
                                            <span key={status} className={`summary-badge status-${status.toLowerCase().replace('-', '')}`}>
                                                {count}
                                            </span>
                                        );
                                    })}
                                </div>
                            )}
                        </div>

                        {!orphanedCollapsed && (
                                <div className="swimlane-columns">
                                    {(showClosedItems ? statuses : activeStatuses).map(status => {
                                        const statusItems = orphanedItems.filter(item => item.status === status);
                                        return (
                                            <KanbanColumn
                                                key={`orphaned-${status}`}
                                                status={status}
                                                droppableId={`orphaned-${status}`}
                                                items={statusItems}
                                                propertiesMDA={propertiesMDA}
                                                navigate={navigate}
                                                needsMyAttention={needsMyAttention}
                                                getItemIcon={getItemIcon}
                                                getItemDisplayText={getItemDisplayText}
                                                getUserDisplayName={getUserDisplayName}
                                                formatCardDate={formatCardDate}
                                                getProjectName={getProjectName}
                                                allUsers={allUsers}
                                                isAssignedToMyDepartment={isAssignedToMyDepartment}
                                                hideHeader={true}
                                            />
                                        );
                                    })}
                                </div>
                        )}
                    </div>
                );
            })()}
        </div>
    );
};

export default RoutineChecksSwimlanes;